import { onMounted, onUnmounted } from 'vue'

/**
 * Composable for managing and activating tracking tags based on cookie consent.
 */
export function useTrackingTags() {
  
  /**
   * Get consent preferences from localStorage
   */
  const getConsentPreferences = () => {
    try {
      const saved = localStorage.getItem('cookie_consent_preferences')
      if (saved) return JSON.parse(saved)
    } catch (e) {
      console.error('Failed to parse cookie consent preferences:', e)
    }
    // Default to only necessary cookies
    return {
      necessary: true,
      analytics: false,
      marketing: false,
      personalization: false
    }
  }

  /**
   * Scan the DOM for scripts/elements that require consent and activate them if allowed.
   */
  const activateScripts = (preferences) => {
    if (!preferences) {
      preferences = getConsentPreferences()
    }

    const selector = 'script[type="text/plain"][data-consent-category], img[data-consent-category][data-src], iframe[data-consent-category][data-src]'
    const elements = document.querySelectorAll(selector)

    elements.forEach(el => {
      const category = el.getAttribute('data-consent-category')
      const isApproved = category && (category === 'necessary' || preferences[category] === true)

      if (isApproved) {
        const tagName = el.tagName.toLowerCase()
        if (tagName === 'script') {
          const newScript = document.createElement('script')
          
          // Copy attributes
          Array.from(el.attributes).forEach(attr => {
            if (attr.name !== 'type') {
              newScript.setAttribute(attr.name, attr.value)
            }
          })
          newScript.setAttribute('type', 'text/javascript')
          newScript.setAttribute('data-activated', 'true')
          
          if (el.innerHTML) {
            newScript.innerHTML = el.innerHTML
          }

          // Replace old script tag with active one
          el.parentNode.replaceChild(newScript, el)
        } else {
          // For image/iframe, restore src from data-src
          const src = el.getAttribute('data-src')
          if (src) {
            el.setAttribute('src', src)
            el.removeAttribute('data-src')
            el.setAttribute('data-activated', 'true')
          }
        }
      }
    })
  }

  // Handle consent updates from CookieBanner
  const handleConsentUpdate = (event) => {
    const preferences = event.detail
    activateScripts(preferences)
  }

  const initTrackingConsent = () => {
    // 1. Initial activation based on current preferences
    const preferences = getConsentPreferences()
    
    // Defer activation until browser is idle to avoid blocking LCP and main-thread
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(() => activateScripts(preferences), { timeout: 2000 })
    } else {
      setTimeout(() => activateScripts(preferences), 1500)
    }

    // 2. Listen for changes in consent
    window.addEventListener('cookieConsentUpdated', handleConsentUpdate)
  }

  const cleanupTrackingConsent = () => {
    window.removeEventListener('cookieConsentUpdated', handleConsentUpdate)
  }

  return {
    initTrackingConsent,
    cleanupTrackingConsent,
    getConsentPreferences,
    activateScripts
  }
}
